import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Spinner, HTMLSelect, MenuItem } from '@blueprintjs/core';
import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplateSection,
  FormTemplateOption
} from 'data/template/template.types';
import { Tooltip2 } from '@blueprintjs/popover2';
import { RootState } from 'data/root.reducers';
import NetworkTopologyGlobeMap from 'components/topology/Map';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as networkTopologyActions from 'data/network-topology/network-topology.actions';
import { deleteNetworkTopologyCustomFilter } from 'data/network-topology/network-topology.api';
import * as networkTopologySelectors from 'data/network-topology/network-topology.selectors';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoveryXcipioSelector from 'xcipio/discovery-xcipio-selectors';
import {
  NetworkTopologyMapData,
  NetworkTopologyFilter,
  newNetworkTopologyFilter,
  MOCK_DATA_ENUM,
  NetworkTopologyMapConfig,
} from 'data/network-topology/network-topology.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { networkTopologyFilterFormTemplate } from '../../templates/networkTopologyFilterFormTemplate';
import { adaptFormTemplateOptions } from '../../utils/adaptFormTemplateOptions';
import NetworkTopologyToolbar from './components/NetworkTopologyToolbar';
import NetworkTopologyClusterExpansionPane from './components/NetworkTopologyClusterExpansionPane';
import './network-topology-map.scss';

type RouterParams = {
  history: string,
}

const mapState = (state: RootState) => ({
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  deliveryFunctionId: state.global.selectedDF,
  networkTopologyMapData: networkTopologySelectors.getNetworkTopologyMapData(state),
  originalNetworkTopologyMapData: networkTopologySelectors.getOriginalNetworkTopologyMapData(state),
  networkTopologyConfig: networkTopologySelectors.getNetworkTopologyConfig(state),
  networkTopologyFilterFormTemplateInfo: networkTopologyFilterFormTemplate,
  locations: networkTopologySelectors.getNetworkTopologyLocations(state),
  cfTypes: networkTopologySelectors.getCfTypes(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  availableCollectionFunction: supportedFeaturesSelectors.getAvailableCollectionFunction(state),
  networkTopologyCustomFilters: networkTopologySelectors.getNetworkTopologyCustomFilters(state),
  selectedNetworkTopologyFilter: networkTopologySelectors.getSelectedNetworkTopologyFilter(state),
  collectionFunctions: networkTopologySelectors.getCollectionFunctionsNetworkTopology(state),
  shouldUseNetworkTopologyMockData: discoveryXcipioSelector.shouldUseNetworkTopologyMockData(state),
  isLoadingAppSettings: discoveryXcipioSelector.isLoadingAppSettings(state),
  isAppSettingLoaded: discoveryXcipioSelector.isAppSettingLoaded(state),
  shouldUseNetworkTopologyDinamicLocations: discoveryXcipioSelector.shouldUseNetworkTopologyDinamicLocations(state),
  isNetworkTopologyLoading: networkTopologySelectors.getNetworkTopologyLoading(state),
  isNetworkTopologyLoaded: networkTopologySelectors.getIsNetworkTopologyLoaded(state),
  zoomInLocarion: networkTopologySelectors.getZoomInLocarion(state),
  networkTopologyMapTileServerURI: discoveryXcipioSelector.getNetworkTopologyMapTileServerURI(state),
  mapDefaultHome: discoveryXcipioSelector.getDiscoverySettingMapDefaultHome(state)
});

const mapDispatch = {
  getNetworkTopologyActions: networkTopologyActions.getNetworkTopologyActions,
  filterNetworkTopologyAction: networkTopologyActions.filterNetworkTopologyAction,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  getNetworkTopologyCustomFilters: networkTopologyActions.getNetworkTopologyCustomFilters,
  createNetworkTopologyFilter: networkTopologyActions.createNetworkTopologyFilter,
  setNetworkTopologyFilter: networkTopologyActions.setNetworkTopologyFilter,
  loadMockDataFileAction: networkTopologyActions.loadMockDataFile,
  getNetworkTopologyConfigActions: networkTopologyActions.getNetworkTopologyConfig
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const NetworkTopologyMap: FunctionComponent<Props> = ({
  deliveryFunctionId,
  networkTopologyMapData,
  originalNetworkTopologyMapData,
  networkTopologyConfig,
  networkTopologyFilterFormTemplateInfo,
  locations,
  cfTypes,
  availableAccessFunction,
  availableCollectionFunction,
  networkTopologyCustomFilters,
  selectedNetworkTopologyFilter,
  shouldUseNetworkTopologyMockData,
  collectionFunctions,
  isNetworkTopologyLoading,
  isAppSettingLoaded,
  isNetworkTopologyLoaded,
  zoomInLocarion,
  getNetworkTopologyActions,
  filterNetworkTopologyAction,
  getSupportedFeature,
  getNetworkTopologyCustomFilters,
  createNetworkTopologyFilter,
  setNetworkTopologyFilter,
  loadMockDataFileAction,
  getNetworkTopologyConfigActions,
  shouldUseNetworkTopologyDinamicLocations,
  isLoadingAppSettings,
  networkTopologyMapTileServerURI,
  mapDefaultHome
}: Props) => {
  const [template, setTemplate] = useState(networkTopologyFilterFormTemplateInfo);
  const [componetNetworkTopologyMapData, setNetworkTopologyMapData] = useState<NetworkTopologyMapData | undefined>(undefined);
  const [customFilters, setCustomFilters] = useState<NetworkTopologyFilter[]>([]);
  const [clusterExpansionList, setClusterExpansionList] = useState<any[]>([]);
  const [filtersData, setFiltersData] = useState<NetworkTopologyFilter>(newNetworkTopologyFilter);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [mustLoadNetworkTopologyData, setMustLoadNetworkTopologyData] = useState<boolean>(true);
  const [mockDataFile, setMockDataFile] = useState<string>('');
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);
  const [mapConfig, setMapConfig] = useState<NetworkTopologyMapConfig | undefined>(undefined)

  useEffect(() => {
    getNetworkTopologyConfigActions();
  }, [])

  useEffect(() => {
    if (isAppSettingLoaded) {
      const configCopy = cloneDeep(networkTopologyConfig);
      setMapConfig(configCopy);
    }
  }, [networkTopologyConfig, isAppSettingLoaded])

  useEffect(() => {
    setCustomFilters(networkTopologyCustomFilters);
  }, [networkTopologyCustomFilters]);

  useEffect(() => {
    getSupportedFeature(deliveryFunctionId);
  }, [deliveryFunctionId, getSupportedFeature]);

  useEffect(() => {
    if (!mustLoadNetworkTopologyData && (isNetworkTopologyLoaded || isNetworkTopologyLoading)) {
      return;
    }

    if (isAppSettingLoaded && mustLoadNetworkTopologyData) {
      getNetworkTopologyActions(deliveryFunctionId, shouldUseNetworkTopologyMockData, shouldUseNetworkTopologyDinamicLocations);
      setMustLoadNetworkTopologyData(false);
    }
  }, [
    deliveryFunctionId,
    shouldUseNetworkTopologyMockData,
    isLoadingAppSettings,
    isNetworkTopologyLoading,
    isAppSettingLoaded,
    isNetworkTopologyLoaded,
    shouldUseNetworkTopologyDinamicLocations,
    getNetworkTopologyActions,
  ]);

  useEffect(() => {
    getNetworkTopologyCustomFilters();
  }, [getNetworkTopologyCustomFilters]);

  useEffect(() => {
    if (networkTopologyMapData?.xcipioNodes?.length === 0 &&
          networkTopologyMapData?.mdf2s?.length === 0 &&
          networkTopologyMapData?.mdf3s?.length === 0 &&
          networkTopologyMapData?.admfs?.length === 0) {
      return;
    }
    let searchHistoryOptions: FormTemplateOption[] = []
    if ((template?.main as FormTemplateSection)?.fields?.search?.options) {
      searchHistoryOptions = (template.main as FormTemplateSection).fields.search.options!
    }
    const clonedTemplate = adaptFormTemplateOptions(
      networkTopologyFilterFormTemplateInfo,
      originalNetworkTopologyMapData,
      locations,
      availableAccessFunction,
      collectionFunctions,
      searchHistoryOptions
    )
    setTemplate(clonedTemplate);
  }, [
    networkTopologyFilterFormTemplateInfo,
    locations,
    cfTypes,
    availableAccessFunction,
    availableCollectionFunction,
    collectionFunctions,
    networkTopologyMapData,
    originalNetworkTopologyMapData,
  ]);

  useEffect(() => {
    setNetworkTopologyMapData(networkTopologyMapData)
  }, [
    networkTopologyMapData,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      // NOTE: this will  force the re render of the component to reset the connection lines
      setNetworkTopologyMapData(undefined);
      filterNetworkTopologyAction({
        filter: filtersData,
        availableAccessFunction,
        availableCollectionFunction,
      });
    }, 500);
    return () => clearTimeout(timer)
  }, [searchFilter]);

  const handleFieldUpdate = ({ name, value }: FieldUpdateHandlerParam) => {
    const newFilterData = {
      ...filtersData,
      [name]: value,
    }
    setFiltersData(newFilterData);

    // NOTE: we are not filtering by every search due preformance issues. It will be done only after user stop typing
    if (name !== 'search') {
      setNetworkTopologyMapData(undefined);
      filterNetworkTopologyAction({
        filter: newFilterData,
        availableAccessFunction,
        availableCollectionFunction,
      });
    } else {
      setSearchFilter(value); // NOTE: this will trigger the useEffect responsble for only filtering after user stop typing
    }
    // Clear the clusterExpansionList if filter changes
    if (clusterExpansionList && clusterExpansionList.length > 0) {
      setClusterExpansionList([]);
    }
  }

  const handleCustomFilterSelection = (selectedFilter?: NetworkTopologyFilter) => {
    if (selectedFilter == null) {
      return;
    }
    setNetworkTopologyMapData(undefined);
    setFiltersData(selectedFilter);
    filterNetworkTopologyAction({
      filter: selectedFilter,
      availableAccessFunction,
      availableCollectionFunction,
    });
  }

  const handleClear = () => {
    if (!Object.keys(filtersData).length) {
      return;
    }
    setNetworkTopologyMapData(undefined);
    setFiltersData(newNetworkTopologyFilter);
    filterNetworkTopologyAction({
      filter: newNetworkTopologyFilter,
      availableAccessFunction,
      availableCollectionFunction,
    });
  }

  const handleClearClusterFilter = () => {
    const newFilterData:NetworkTopologyFilter = {
      ...filtersData,
      poiDataType: '',
      id: ''
    }
    setNetworkTopologyMapData(undefined);
    filterNetworkTopologyAction({
      filter: newFilterData,
      availableAccessFunction,
      availableCollectionFunction,
    });
  }

  const handleClickExport = () => {
    console.log('handleClickExport');
  }

  const handleRemoveView = (id: string) => {
    deleteNetworkTopologyCustomFilter(id).then(() => {
      getNetworkTopologyCustomFilters();
      handleClear();
    });
  }

  const handleSave = (name: string) => {
    createNetworkTopologyFilter({
      ...filtersData,
      name,
    }).then((x) => {
      getNetworkTopologyCustomFilters();
    });
  }

  const shouldRenderMapComponent = () => {
    if (!isAppSettingLoaded || isLoadingAppSettings || !componetNetworkTopologyMapData?.xcipioNodes || !mapConfig) {
      return false;
    }

    if (
      shouldUseNetworkTopologyMockData &&
      mustLoadNetworkTopologyData
    ) {
      return false
    }

    return true;
  }

  const handleBlur = (e: any) => {
    if (e?.currentTarget?.name === 'search' && e?.currentTarget?.value.length > 0) {
      // We keep a search history for the string seach field
      const stringSearchOptions = (template.main as FormTemplateSection).fields.search.options!
        .filter((x: FormTemplateOption) => x.value !== e?.currentTarget?.value)
        .map((x: FormTemplateOption) => {
          return {
            label: x.value,
            value: x.value
          };
        });

      stringSearchOptions.unshift({
        label: e?.currentTarget?.value,
        value: e?.currentTarget?.value,
      });

      if (stringSearchOptions.length > 10) {
        stringSearchOptions.splice(10);
      }
      const formTemplateSection = (template.main as FormTemplateSection)
      setTemplate({
        ...template,
        main: {
          ...formTemplateSection,
          fields: {
            ...formTemplateSection.fields,
            search: {
              ...formTemplateSection.fields.search,
              options: stringSearchOptions
            }
          }
        }
      });
    }
  }

  const handleFilterPoi = ({ id, type }: { id: string, type: string }) => {
    const newFilterData = {
      ...filtersData,
      id,
      poiDataType: type
    }

    setNetworkTopologyMapData(undefined);
    filterNetworkTopologyAction({
      filter: newFilterData,
      availableAccessFunction,
      availableCollectionFunction,
    });
  }

  const mockDataChange = (event: any) => {
    if (event.target.value) {
      setNetworkTopologyMapData(undefined);
      setMockDataFile(event.target.value);
      loadMockDataFileAction(event.target.value);
    }
  }

  const renderDropdown = () => {
    const options = [
      { value: '', label: 'Select mock data file' },
      // { value: MOCK_DATA_ENUM.MOCK_1_AF_1_CF_1_XCIPIO, label: '1 AF, 1 CF, 2 XCIPIO' },
      // { value: MOCK_DATA_ENUM.MOCK_10_AFS_10_CFS_1_CCPAG, label: '10 Afs, 10 Cfs, 1 CCPAG' },
      { value: MOCK_DATA_ENUM.MOCK_100_AFS_100_CFS_10_CCPAGS, label: '100 Afs 100 Cfs 50 CCPAG' },
      { value: MOCK_DATA_ENUM.MOCK_500_AFS_500_CFS_50_CCPAGS, label: '500 Afs 500 Cfs 50 CCPAG' },
      { value: MOCK_DATA_ENUM.MOCK_1000_AFS_1000_CFS_50_CCPAGS, label: '1000 Afs 1000 Cfs 50 CCPAG' },
      // { value: MOCK_DATA_ENUM.MOCK_LINE_DIRECTIONS, label: 'Line directions' },
      // { value: MOCK_DATA_ENUM.MOCK_MARKER_COLLISIONS, label: 'Marker Collisions' },
      // { value: MOCK_DATA_ENUM.MOCK_LINES, label: 'Lines orientation' },
      // { value: MOCK_DATA_ENUM.MOCK_LINK_POPUPS, label: 'Demo MDF2/MDF3' },
    ];

    return (
      <HTMLSelect
        options={options}
        onChange={mockDataChange}
        value={mockDataFile}
      />
    );
  }

  return (
    <div data-testid="NetworkTopologyMap" className="network-topology-map-container">
      {shouldUseNetworkTopologyMockData && renderDropdown()}
      <NetworkTopologyToolbar
        template={template}
        filters={customFilters}
        data={filtersData}
        onClickExport={handleClickExport}
        onFieldUpdate={handleFieldUpdate}
        onFilterSelection={handleCustomFilterSelection}
        onRemoveView={handleRemoveView}
        onClear={handleClear}
        onSave={handleSave}
        handleBlur={handleBlur}
      />
      <div data-testid="NetworkTopologyMap" className="network-topology-map" style={{ display: 'grid', gridTemplateColumns: clusterExpansionList && clusterExpansionList.length > 0 ? 'minmax(220px, 1fr) 4fr' : '0 1fr' }}>
        <div>
          {(clusterExpansionList) &&
          (clusterExpansionList.length > 0) && (
            <NetworkTopologyClusterExpansionPane
              setClusterExpansionList={setClusterExpansionList}
              clusterExpansionList={clusterExpansionList}
              onFilterPoi={handleFilterPoi}
              onCloseSideMenu={handleClearClusterFilter}
            />
            )}
        </div>
        {(shouldRenderMapComponent()) && (
          <NetworkTopologyGlobeMap
            mapConfig={mapConfig}
            mapData={componetNetworkTopologyMapData as NetworkTopologyMapData}
            selectedBaseMap={networkTopologyMapTileServerURI ?? networkTopologyConfig?.mapBaseLayer['Basemaps-Cartocdn'].url}
            zoomInLocarion={zoomInLocarion}
            setClusterExpansionList={setClusterExpansionList}
          />
        )}
      </div>
    </div>
  );
};

export default withRouter(connector(NetworkTopologyMap));
